import { getEnvironment } from "@utils/configuration/aem";
import { Environments } from "@utils/general";

import { WindowKeys, WindowManagement } from "./windowManagement";

const FEATURE_TOGGLE_PREFIX = "hubo.feature.";

export enum FeatureToggleKeys {
    ANALYTICS_LOGGING = "analytics.logging",
    EVENT_DATA_LAYER_LOGGING = "eventDataLayer.logging",
    NEVERBOUNCE = "neverbounce",
    PRODUCT_COMPARISON = "product.comparison",
    CART_ITEM_PROMOTION_LABEL = "cart.item.promotion.label",
    WISHLIST_BADGE = "wishlist.badge",
    AUTHORISATION_MODE = "authorisation.mode",
    INSTANT_MOBILE_QR = "payment.instant-mobile-qr",
    ALGOLIA_DEBUG = "algolia",
    STICKY_ECOMMERCE = "ecommerce.sticky",
    FEATURE_SWITCH_UI = "feature.switch.ui",
    REVIEWS = "reviews",
}

export const FeatureToggleEventName = "onFeatureToggle";

export class FeatureToggleEvent extends Event {
    private _key: string;
    private _enabled: boolean;

    constructor(key: FeatureToggleKeys, enabled: boolean) {
        super(FeatureToggleEventName);
        this._key = key;
        this._enabled = enabled;
    }

    get key() {
        return this._key;
    }

    get isEnabled() {
        return this._enabled;
    }
}

interface FeatureToggle {
    name: FeatureToggleKeys;
    enabled: boolean;
    data?: any;
    dateChanged?: number;
}

const Config: Record<string, boolean> = {
    [FeatureToggleKeys.ANALYTICS_LOGGING]: false,
    [FeatureToggleKeys.EVENT_DATA_LAYER_LOGGING]: false,
    [FeatureToggleKeys.NEVERBOUNCE]: false,
    [FeatureToggleKeys.PRODUCT_COMPARISON]: true,
    [FeatureToggleKeys.WISHLIST_BADGE]: false,
    [FeatureToggleKeys.AUTHORISATION_MODE]: false,
    [FeatureToggleKeys.INSTANT_MOBILE_QR]: false,
    [FeatureToggleKeys.ALGOLIA_DEBUG]: false,
    [FeatureToggleKeys.STICKY_ECOMMERCE]: false,
    [FeatureToggleKeys.FEATURE_SWITCH_UI]: false,
    [FeatureToggleKeys.REVIEWS]: false,
};

export default class FeatureToggles {
    static isEnabled(
        name: FeatureToggleKeys,
        envOverrides: Array<Environments> = [],
    ): boolean {
        return (
            FeatureToggles.getFeature(name).enabled ||
            envOverrides.some(e => e === getEnvironment())
        );
    }

    static setFeature(
        name: FeatureToggleKeys,
        enabled: boolean = false,
        data?: any,
    ): void {
        if (!name) return;

        const currentFlag = FeatureToggles.getFeature(name);

        localStorage.setItem(
            FEATURE_TOGGLE_PREFIX + name,
            JSON.stringify({
                ...currentFlag,
                data: { ...currentFlag.data, ...data },
                enabled,
                dateChanged: Date.now(),
            }),
        );

        window.dispatchEvent(new FeatureToggleEvent(name, enabled));
    }

    static hasFeature(name: FeatureToggleKeys): boolean {
        try {
            const featureString = localStorage.getItem(
                FEATURE_TOGGLE_PREFIX + name,
            );
            return !!featureString;
        } catch (e) {
            return false;
        }
    }

    static getFeature(name: FeatureToggleKeys): FeatureToggle {
        try {
            const featureString = localStorage.getItem(
                FEATURE_TOGGLE_PREFIX + name,
            );

            if (featureString) {
                return JSON.parse(featureString);
            }

            return {
                name,
                enabled: !!Config[name],
            };
        } catch (e) {
            return {
                name,
                enabled: false,
            };
        }
    }
}

const init = () => {
    WindowManagement.saveObject(WindowKeys.FEATURE_TOGGLES, FeatureToggles);
};

export { init };
